import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { switchMap, combineLatestWith, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserService } from '../../user/user.service';

/**
 * URL màn login kèm đích đến (nếu đích đó đáng giữ).
 *
 * Bỏ qua khi đích là gốc hoặc chính màn login: `''` đã `redirectTo: 'login'` nên giữ `/`
 * chỉ làm hỏng nhánh "chưa có tenant → /onboarding" mà `submit()` đang lo, còn giữ `/login`
 * thì tự quay vòng vào chính nó.
 */
const buildLoginUrl = (target: string | undefined): string => {
  const keepTarget = !!target && target !== '/' && !target.startsWith('/login');
  return keepTarget ? `/login?redirectUrl=${encodeURIComponent(target)}` : 'login';
};

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.check().pipe(
    combineLatestWith(userService.isAdmin$, userService.resources$),
    take(1),
    switchMap(([authenticated, isAdmin, resources]) => {
      if (!authenticated) {
        // GIỮ LẠI đích đến. Màn login đã đọc `?redirectUrl=` từ đầu nhưng KHÔNG chỗ nào
        // sinh ra tham số đó, nên mọi deep link đều rơi về mặc định (/profile | /onboarding).
        // Hệ quả thấy rõ nhất: nút "Giao diện mobile" ở màn login gọi navigate('/mobile'),
        // guard này đá thẳng về /login và cú bấm trông như CHẾT — chỉ còn lại `viewPref`
        // trong localStorage, mãi tới lần đăng nhập sau mới có tác dụng.
        router.navigateByUrl(buildLoginUrl(state.url));
        return of(false);
      }

      // ✅ Check isAdmin trước, nếu là admin thì bypass tất cả
      if (isAdmin) {
        return of(true);
      }

      // Sau đó mới check resourceUniqueCode
      const resourceUniqueCode = route.data['resourceUniqueCode'];
      if (!resourceUniqueCode) {
        return of(true);
      }

      if (!userService.hasResource(resourceUniqueCode)) {
        router.navigateByUrl('/not-authorized');
        return of(false);
      }

      return of(true);
    }),
  );
};
